import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

export default class DrawerController extends Controller {
  static values = {
    mobileSize: String,
    swipeThreshold: { type: Number, default: 100 }
  }

  #resizeHandler = null
  #defaultSize = null

  connect () {
    this.element.addEventListener('click', this.#closeOnBackdropClick)
    this.element.addEventListener('touchstart', this.#onTouchStart, { passive: true })
    this.element.addEventListener('touchmove', this.#onTouchMove, { passive: true })
    this.element.addEventListener('touchend', this.#onTouchEnd, { passive: true })
    if (this.hasMobileSizeValue) {
      this.#defaultSize = this.#currentSizeClass()
      this.#handleResize()
      this.#resizeHandler = this.#handleResize.bind(this)
      window.addEventListener('resize', this.#resizeHandler)
    }
    this.element.showModal()
  }

  disconnect () {
    this.element.removeEventListener('click', this.#closeOnBackdropClick)
    this.element.removeEventListener('touchstart', this.#onTouchStart)
    this.element.removeEventListener('touchmove', this.#onTouchMove)
    this.element.removeEventListener('touchend', this.#onTouchEnd)
    if (this.#resizeHandler) window.removeEventListener('resize', this.#resizeHandler)
    this.close()
  }

  show () {
    this.element.showModal()
  }

  close () {
    try {
      this.element.close()
      DrawerController.turboFrame.src = null
      this.element.remove()
    } catch (e) {}
  }

  #handleResize () {
    this.#applySize(window.innerWidth < 768 ? this.mobileSizeValue : this.#defaultSize)
  }

  #currentSizeClass () {
    const allSizes = ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', 'page']
    if (this.element.classList.contains('drawer--page')) return 'page'
    return allSizes.find(s => this.element.classList.contains(`w-${s}`))
  }

  #applySize (size) {
    const allSizes = ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', 'page']
    this.element.classList.remove('drawer--page', ...allSizes.map(s => `w-${s}`))
    size === 'page' ? this.element.classList.add('drawer--page') : this.element.classList.add(`w-${size}`)
  }

  #closeOnBackdropClick = (event) => {
    if (event.target === this.element) {
      this.close()
    }
  }

  #onTouchStart = (event) => {
    this.touchStartX = event.touches[0].clientX
    this.element.style.transition = 'none'
  }

  #onTouchMove = (event) => {
    const currentX = event.touches[0].clientX
    const diff = Math.max(0, currentX - this.touchStartX)
    this.element.style.transform = `translateX(${diff}px)`
  }

  #onTouchEnd = (event) => {
    const touchEndX = event.changedTouches[0].clientX
    const diff = touchEndX - this.touchStartX

    this.element.style.transition = 'transform 0.2s ease-out'

    if (diff > this.swipeThresholdValue) {
      this.element.style.transform = 'translateX(100%)'
      this.element.addEventListener('transitionend', () => this.close(), { once: true })
    } else {
      this.element.style.transform = 'translateX(0)'
    }
  }

  static get turboFrame () {
    return document.querySelector('turbo-frame[id=\'drawer\']')
  }
}

stimulus.register('drawer', DrawerController)
